import { type PowerSyncDrizzleDB, user } from "@boveda/powersync";
import { patchRowVerified } from "../powersync/verified-write";

/**
 * ── Why ALL mutations below pass through `patchRowVerified` ───────────────
 *
 * A Drizzle `UPDATE … WHERE id = ?` against PowerSync view can
 * match ZERO rows and return "success" if row has not yet landed from
 * checkpoint (see `powersync/verified-write.ts`). `user` is NOT exempt from
 * this window despite being a 1-row-per-user table: `ThemeProvider`
 * (`apps/web/src/providers/theme-provider.tsx`) seeds `user` row with a
 * local `db.insert(user)` for visitor (`uid === VISITOR_ID`; before
 * 2026-08-13 guard it did so for ANY `uid`, including freshly created
 * real account — that was root cause, now closed). Changing language or
 * theme right after that seed falls into the same window as any other
 * mutation; why protection is still needed despite guard: see
 * `powersync/verified-write.ts`.
 *
 * ── Why ALL mutations below carry `.where(eq(user.id, userId))` ─
 *
 * Four of them (`locale`, `global_theme_id`, `is_blur`,
 * `follow_device_theme`) had this shape:
 *
 *     const query = db.update(user).set({ … });
 *     await (userId === VISITOR_ID ? query : query.where(eq(user.id, userId)));
 *
 * meaning that, in guest mode, the UPDATE went out **without `WHERE`** → it rewrote ALL
 * `user` rows in the local database. The branch existed for symmetry with
 * `getUserThemeSettingsQuery`, which for the guest reads with `.limit(1)` and no
 * filter; but a read `limit(1)` and an unfiltered UPDATE are not the same
 * bet: the former can pick the wrong row, while the latter crushes all of them.
 *
 * The premise behind it ("the guest database has only one
 * `user` row") is false in a specific window: during the guest→real upgrade, the
 * guest database CONNECTS to the real account's connector and
 * self-subscribes to all streams—including `organizations`, which syncs
 * one `user` row for EACH co-member of that account's organizations.
 * There the table has 1 + 1 + N rows, `user` is NOT `localOnly`, and each touched
 * row enqueues its own op in `ps_crud`.
 *
 * The correct filter is NOT guessed: `ThemeProvider`
 * (`apps/web/src/providers/theme-provider.tsx`) seeds the guest row with
 * `db.insert(user).values({ id: uid, … })`, and for a guest tab `uid` is exactly
 * `VISITOR_ID`. Thus `eq(user.id, userId)` points to the SAME row that the
 * unfiltered branch intended to touch when the table had only one row —
 * identical behavior in the normal case, bounded in the rest. It is also the
 * form already used by `updateUserFontFamilyMutation` and
 * `updateUserImageMutation`, which never had a guest branch.
 *
 * Guarded by `packages/shared/src/users/__tests__/mutations-where.test.ts`.
 */

/**
 * Shared Drizzle mutation for updating User locale.
 */
export async function updateUserLocaleMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  locale: string,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { locale });
}

/**
 * Shared Drizzle mutation for updating User font family preference.
 */
export async function updateUserFontFamilyMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  fontFamilyId: string | null,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { font_family: fontFamilyId });
}

/**
 * Writes the avatar KEY to the local row.
 *
 * It is written local-first (like language, font, and theme), not through REST:
 * the new photo appears instantly and survives a cold reload while offline. The
 * BINARY does go through REST (`POST /api/avatar`) because it does not fit in
 * the sync queue; only the key returned by that upload travels through here.
 *
 * `null` = "remove the photo" (the initials return).
 *
 * A guest has no syncable row of its own and cannot have an avatar (zero rule),
 * so there is no `VISITOR_ID` branch here as there is for `locale`/`theme`.
 */
export async function updateUserImageMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  imageKey: string | null,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { image: imageKey });
}

/**
 * Shared Drizzle mutation for updating global theme.
 */
export async function updateGlobalThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  themeId: string | null,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { global_theme_id: themeId });
}

/**
 * Shared Drizzle mutation for updating blur effect preference.
 */
export async function updateBlurMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  isBlur: boolean,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { is_blur: isBlur ? 1 : 0 });
}

/**
 * Shared Drizzle mutation for updating follow device appearance preference.
 */
export async function updateFollowDeviceThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  followDevice: boolean,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, {
    follow_device_theme: followDevice ? 1 : 0,
  });
}
